import { useProducts } from "../../lib/queries";
import type { CaseCard, CaseKind } from "../../lib/types";
import { KIND_LABEL } from "./case-text";

export type CaseDraft = Pick<
  CaseCard,
  | "kind"
  | "title"
  | "description"
  | "productId"
  | "model"
  | "serial"
  | "purchaseDate"
  | "purchasePlace"
  | "invoice"
  | "reference"
>;

export const EMPTY_CASE: CaseDraft = {
  kind: "warranty",
  title: "",
  description: null,
  productId: null,
  model: null,
  serial: null,
  purchaseDate: null,
  purchasePlace: null,
  invoice: null,
  reference: null,
};

/** Kinds in the order a workshop meets them. */
const KINDS: CaseKind[] = ["warranty", "repair", "exchange", "return", "other"];

/** What the case is about: the same fields when opening it and on its page. */
export function CaseFields({
  value: d,
  onChange,
  idPrefix,
  disabled = false,
}: {
  value: CaseDraft;
  onChange: (d: CaseDraft) => void;
  idPrefix: string;
  disabled?: boolean;
}) {
  // Services are not repaired: the field lists the catalog's products, inactive ones too.
  const products = useProducts("", "", { kind: "product", withInactive: true }).data?.items ?? [];
  const set = <K extends keyof CaseDraft>(k: K, v: CaseDraft[K]) => onChange({ ...d, [k]: v });
  const txt = (k: keyof CaseDraft) => (d[k] as string | null) ?? "";
  const id = (name: string) => `${idPrefix}-${name}`;
  return (
    <fieldset className="case-fields" disabled={disabled}>
      <div className="field">
        <label htmlFor={id("title")}>Qué pasa</label>
        <input
          className="input"
          id={id("title")}
          placeholder="Ej.: La lavadora gotea por debajo"
          required
          maxLength={200}
          value={d.title}
          onChange={(e) => set("title", e.target.value)}
        />
      </div>
      <div className="two-cols">
        <div className="field">
          <label htmlFor={id("kind")}>Tipo</label>
          <select
            className="select"
            id={id("kind")}
            value={d.kind}
            onChange={(e) => set("kind", e.target.value as CaseKind)}
          >
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor={id("product")}>Producto</label>
          <select
            className="select"
            id={id("product")}
            value={d.productId ?? ""}
            onChange={(e) => set("productId", e.target.value || null)}
          >
            <option value="">No está en el catálogo</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.model ? `${p.name} (${p.model})` : p.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="two-cols">
        <div className="field">
          <label htmlFor={id("model")}>{d.productId ? "Modelo (si es otro)" : "Modelo"}</label>
          <input
            className="input"
            id={id("model")}
            placeholder="Ej.: LV-20"
            maxLength={120}
            value={txt("model")}
            onChange={(e) => set("model", e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={id("serial")}>Número de serie</label>
          <input
            className="input"
            id={id("serial")}
            placeholder="Ej.: SN 2024-001234"
            maxLength={120}
            value={txt("serial")}
            onChange={(e) => set("serial", e.target.value)}
          />
        </div>
      </div>
      <div className="two-cols">
        <div className="field">
          <label htmlFor={id("date")}>Fecha de compra</label>
          <input
            className="input"
            id={id("date")}
            type="date"
            value={txt("purchaseDate")}
            onChange={(e) => set("purchaseDate", e.target.value || null)}
          />
        </div>
        <div className="field">
          <label htmlFor={id("place")}>Dónde lo compró</label>
          <input
            className="input"
            id={id("place")}
            placeholder="Ej.: Tienda de Liberia"
            maxLength={200}
            value={txt("purchasePlace")}
            onChange={(e) => set("purchasePlace", e.target.value)}
          />
        </div>
      </div>
      <div className="two-cols">
        <div className="field">
          <label htmlFor={id("invoice")}>Factura</label>
          <input
            className="input"
            id={id("invoice")}
            placeholder="Ej.: 00100001010000012345"
            maxLength={120}
            value={txt("invoice")}
            onChange={(e) => set("invoice", e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={id("reference")}>Referencia interna (opcional)</label>
          <input
            className="input"
            id={id("reference")}
            placeholder="Ej.: OT-1543"
            maxLength={120}
            value={txt("reference")}
            onChange={(e) => set("reference", e.target.value)}
          />
        </div>
      </div>
      <div className="field">
        <label htmlFor={id("description")}>Detalle</label>
        <textarea
          className="textarea"
          id={id("description")}
          rows={3}
          placeholder="Ej.: Empezó a gotear después de 3 meses de uso. El cliente manda fotos por WhatsApp."
          maxLength={4000}
          value={txt("description")}
          onChange={(e) => set("description", e.target.value)}
        />
      </div>
    </fieldset>
  );
}
